// İSG uzmanı portalı — "Karar" paneli (S32b `docs/reviews/S32b-gate-desk-relayout.md` Option A
// §2). 1/2·1/2 çalışma ızgarasının ÜSTÜNDE, tam genişlikte, HER ZAMAN görünen tek bant — kapı
// kodu okutması VE TC sorgusu `useVerdictStage.ts`'e AYNI sahneyi paylaştırır, bu yüzden verdict
// artık bir sekmenin İÇİNDE değildir ve sekme çalınması sorunu (§2.1) kökten ORTADAN KALKAR.
//
// `rounded-sm` + panel dili (`PanelHeader`, `docs...` §3) — `ShieldCheck` ikonu Yönetim nav
// ikonuyla ÇAKIŞIR (güvenlik o girişi hiç görmez; İSG uzmanı ikisini de görür ama FARKLI
// yerlerde) — belgede adı geçen, kabul edilen bir çakışma (§3.3), İCAT EDİLMİŞ değil.
//
// Panel gövdesi SABİT bir taban yükseklik taşır — boş/karar durumları arasında panel ZIPLAMAZ
// (S32 §4'ün "fixed-height stage" ilkesi, S32b'de HÂLÂ bağlayıcı).
//
// **S32c cila turu (`docs/reviews/S32c-gate-desk-polish.md` §2.4/§4.4):**
//   - Kuyruk rozeti (`Badge tone="warning"` + `Users`) panel BAŞLIĞINDAN TAMAMEN KALKTI —
//     sayı artık YALNIZ `VerdictCard`'ın düğmesinde (`Sıradaki (N)`, §4). İKİ ayrı kuyruk
//     göstergesi YOKTUR artık; düğme kazanır çünkü sayı ORADA aksiyona alınabilir (§4.4).
//
// **S32d yeniden çizimi (`docs/reviews/S32d-verdict-and-detail.md` §2.1/F3) — taban yükseklik
// `272px` → `354px`.** `VerdictCard.tsx`'in ÜÇ sabit satırının (kimlik+karar şeridi `11rem` +
// kanıt şeridi `7.5rem` + eylem çubuğu `3.5rem` = `18.5rem` = `296px`, artı iki `border-t`
// çizgisi) TOPLAMINDAN türetildi — S32c'nin AYNI uyarısı burada da geçerli: bu ARİTMETİKTİR,
// `web-qa-engineer` dört varyantı (+ 0/1/2/4 izinle `allowed`) gerçek pikselle DOĞRULAMALI (F3).
// `nextIsDenial` (`useVerdictStage.ts`) buradan `VerdictCard`'a akar — S32c §4.5'in "taşınmış"
// ret-önizleme ikonu.
//
// **S32e Part Two (`docs/reviews/S32e-evidence-and-layout.md` §4.3 madde 2) — panel `h-full`,
// gövde `flex-1`.** `SecuritySiteSession.tsx`'in `[Karar | Giriş]` ızgarasında bu panel `Giriş`
// ile AYNI satırda, ızgaranın varsayılan `items-stretch`'i panelin YÜKSEKLİĞİNİ satırın (yani
// `GateStage`'in sabitlenmiş taban yüksekliğinin, G7) yüksekliğine EŞİTLER. `min-h-[354px]` bir
// TABAN olarak KALIR (kısa varyantlarda/boşken panel küçülmez) ama artık `flex-1` — panel daha
// uzun bir satıra DÜŞTÜĞÜNDE gövde onu DOLDURUR, `VerdictCard`'ın kendi `h-full`'ü (S32e notu,
// `VerdictCard.tsx`) bu yüksekliği DEVRALIR. Sabit geometri böylece GÜÇLENİR: kartın yüksekliği
// artık panelin, panelin de satırın yüksekliğidir — dört varyantta, her izin sayısında VE
// sahne/boş geçişinde SABİT (S32c E5'in aritmetik-doğrulama uyarısı hâlâ geçerli).

import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PanelHeader } from '../../components/PanelHeader';
import type { StagedVerdict } from '../../lib/useVerdictStage';
import { VerdictCard, type VerdictCardProps } from './VerdictCard';

function toCardProps(
  entry: StagedVerdict,
  selectedSiteName: string,
): Omit<VerdictCardProps, 'onAcknowledge' | 'className' | 'queuedCount' | 'nextIsDenial'> {
  if (entry.source === 'gate') {
    const { scan } = entry;
    return {
      variant: scan.allowed ? 'allowed' : 'denied',
      personnelName: scan.personnelName,
      // Backend bu dilimi (S32c) henüz indirmemişse alan `undefined` gelebilir — `?? null`
      // (`security.ts` dosya başı notu; `VerdictCard` `null`'ı nötr tireye çevirir).
      contractorName: scan.contractorName ?? null,
      siteName: scan.siteName,
      selectedSiteName,
      reason: scan.reason,
      scannedAt: scan.scannedAt,
      method: scan.method,
      permits: scan.permits,
    };
  }
  if (entry.source === 'tc') {
    const { result } = entry;
    if (!result.found) {
      return { variant: 'notFound' };
    }
    return {
      variant: result.allowed ? 'allowed' : 'denied',
      personnelName: result.personnelName,
      contractorName: result.contractorName ?? null,
      siteName: selectedSiteName,
      selectedSiteName,
      reason: result.reason,
      scannedAt: entry.at,
      method: 'TcLookup',
      permitTitle: result.permitTitle,
    };
  }
  if (entry.source === 'verify') {
    // Akış A (`ScanTab.tsx`) — personel QR'ı okundu. TC'den FARKI: `found` YOKTUR (jeton ya çözülür
    // ya `TokenInvalid`), `reason` JETON ailesinden de olabilir (`VerdictCard`'ın genişletilmiş
    // `reason` tipi). `siteName` sunucudan gelir (jeton sahayı çözer — `selectedSiteName` DEĞİL).
    // `TokenInvalid`'de `personnelName`/`contractorName`/`siteName` `null` (KVKK, §29.5) — kart
    // kimlik döşemesini boş bir tile'a düşürür ("?" YOK, `Avatar.tsx`).
    const { result } = entry;
    return {
      variant: result.allowed ? 'allowed' : 'denied',
      personnelName: result.personnelName,
      contractorName: result.contractorName ?? null,
      siteName: result.siteName,
      selectedSiteName,
      reason: result.reason,
      scannedAt: entry.at,
      method: 'PersonnelQr',
      permitTitle: result.permitTitle,
    };
  }
  return { variant: 'networkError' };
}

export interface VerdictPanelProps {
  stage: StagedVerdict | null;
  queuedCount: number;
  /** S32d §3.4 — kuyruğun başındaki öğe bir ret mi (`useVerdictStage.ts`). */
  nextIsDenial: boolean;
  selectedSiteName: string;
  onAcknowledge: () => void;
}

export function VerdictPanel({
  stage,
  queuedCount,
  nextIsDenial,
  selectedSiteName,
  onAcknowledge,
}: VerdictPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col rounded-sm border border-border bg-surface shadow-sm">
      <PanelHeader icon={ShieldCheck} title={t('security.panel.decision.title')} />
      <div className="flex min-h-[354px] flex-1 flex-col justify-center">
        {stage ? (
          <VerdictCard
            key={stage.key}
            {...toCardProps(stage, selectedSiteName)}
            onAcknowledge={onAcknowledge}
            queuedCount={queuedCount}
            nextIsDenial={nextIsDenial}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 px-5 py-8 text-center">
            <ShieldCheck className="h-8 w-8 text-muted" aria-hidden="true" />
            <p className="text-sm font-medium text-fg">{t('security.verdict.idle.title')}</p>
            <p className="max-w-sm text-sm text-muted">{t('security.verdict.idle.body')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
